/**
 * Permission Handler Hook
 *
 * Centralized permission error handling with toast notifications.
 * Listens for 'app:permission-required' events dispatched by apiClient
 * and displays user-friendly error messages.
 *
 * Features:
 * - Toast notifications for non-blocking errors
 * - Modal fallback for critical operations
 * - Automatic deduplication (same error won't show twice)
 * - Audit logging for HIPAA compliance
 * - Accessible with screen reader support
 *
 * Usage:
 * ```tsx
 * import { usePermissionHandler } from '@shared/hooks/usePermissionHandler';
 *
 * function App() {
 *   usePermissionHandler(); // Mount once in root component
 *   return <YourApp />;
 * }
 * ```
 *
 * @see {@link file://./../config/permissionMessages.ts} Message Configuration
 * @see {@link file://./../services/apiClient.ts} API Client (event dispatcher)
 */

import { useEffect, useCallback } from "react";
import { toast, ToastOptions } from "react-toastify";
import apiClient from "../services/apiClient";
import {
  getPermissionMessage,
  PermissionErrorDetail,
} from "@shared/config/permissionMessages";
import { useAuth } from "@shared/context/AuthContext";
import { trackSuppressedFeatureDenial } from "@shared/services/featureDenialTelemetry";
import { SUPPRESSED_FEATURE_DENIAL_CODES } from "@shared/constants/suppressedFeatureDenialCodes";
import logger from "@shared/utils/logger";

// Track recently shown errors to prevent duplicates
const recentErrors = new Set<string>();
const ERROR_COOLDOWN_MS = 5000; // Don't show same error twice within 5 seconds
const EXPECTED_FEATURE_DENIAL_CODES = SUPPRESSED_FEATURE_DENIAL_CODES;

interface UsePermissionHandlerOptions {
  /**
   * Enable/disable the permission handler
   * @default true
   */
  enabled?: boolean;

  /**
   * Custom toast options
   */
  toastOptions?: Partial<ToastOptions>;

  /**
   * Callback when permission error occurs (for analytics)
   */
  onPermissionDenied?: (detail: PermissionErrorDetail) => void;

  /**
   * Whether to log errors to console (development only)
   * @default process.env.NODE_ENV === 'development'
   */
  logToConsole?: boolean;
}

/**
 * Hook to handle permission errors with toast notifications
 */
export function usePermissionHandler(
  options: UsePermissionHandlerOptions = {},
) {
  const {
    enabled = true,
    toastOptions = {},
    onPermissionDenied,
    logToConsole = process.env.NODE_ENV === "development",
  } = options;
  const { isAuthenticated } = useAuth();

  const handlePermissionError = useCallback(
    async (event: Event) => {
      if (!enabled) return;

      try {
        const customEvent = event as CustomEvent<PermissionErrorDetail>;
        const detail = customEvent.detail;
        const denialCode = String(
          detail.response?.code ||
            detail.response?.error_code ||
            detail.response?.error ||
            "",
        ).trim();
        const isExpectedFeatureGateDeny =
          EXPECTED_FEATURE_DENIAL_CODES.has(denialCode);

        // Generate unique key for this error
        const errorKey = `${detail.url}-${detail.method}`;

        // Check if we've shown this error recently
        if (recentErrors.has(errorKey)) {
          if (logToConsole) {
            logger.info("[Permission] Suppressing duplicate error:", errorKey);
          }
          return;
        }

        // Mark error as shown
        recentErrors.add(errorKey);
        setTimeout(() => {
          recentErrors.delete(errorKey);
        }, ERROR_COOLDOWN_MS);

        // Get user-friendly message
        const { title, message, icon } = getPermissionMessage(detail);

        if (!detail.suppressGlobalNotice && !isExpectedFeatureGateDeny) {
          toast.error(
            <div className="permission-toast">
              <div className="permission-toast__header">
                <span
                  className="permission-toast__icon"
                  role="img"
                  aria-label="Warning"
                >
                  {icon}
                </span>
                <strong>{title}</strong>
              </div>
              <div className="permission-toast__body">{message}</div>
            </div>,
            {
              position: "top-right",
              autoClose: 7000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              toastId: errorKey, // Prevent duplicate toasts
              className: "permission-toast-container",
              ...toastOptions,
            },
          );
        }

        // Log to console in development
        if (logToConsole) {
          logger.info("🔒 [Permission Denied]");
          logger.info("URL:", detail.url);
          logger.info("Method:", detail.method);
          logger.info("Response:", detail.response);
          logger.info("Message:", message);
          // logger.groupEnd();
        }

        // Callback for analytics/logging
        if (onPermissionDenied) {
          onPermissionDenied(detail);
        }

        const currentPath =
          typeof window !== "undefined" ? window.location.pathname : "";
        const isAuthPage =
          currentPath.startsWith("/login") ||
          currentPath.startsWith("/forgot-password") ||
          currentPath.startsWith("/reset-password");
        const statusCode = Number((detail as any)?.status || 0);

        // Send to audit log service for HIPAA compliance.
        // Skip when user is not authenticated (e.g. login screen control-flow),
        // or when the upstream response itself is unauthorized.
        try {
          if (!isAuthPage && isAuthenticated && statusCode !== 401) {
            if (isExpectedFeatureGateDeny) {
              const suppressedDetail = {
                code: denialCode,
                url: detail.url,
                method: detail.method,
                status: statusCode,
                feature: detail.response?.feature,
                organization_id:
                  detail.response?.organization_id ||
                  detail.response?.organizationId,
                required_permission: detail.response?.required_permission,
                timestamp: new Date().toISOString(),
              };
              window.dispatchEvent(
                new CustomEvent("app:feature-denial-audit-suppressed", {
                  detail: suppressedDetail,
                }),
              );
              return;
            }
            const resource =
              detail.response?.resource || detail.url || "unknown";
            const action = detail.method || "UNKNOWN";
            await apiClient.post("/audit", {
              action: "PERMISSION_DENIED",
              resource_type: resource,
              resource_id: null,
              action_type: action,
              outcome: "DENIED",
              ip_address: null, // Backend will capture from request
              metadata: {
                resource,
                action,
                required_permission: detail.response?.required_permission,
                error_code: detail.response?.error_code,
                timestamp: new Date().toISOString(),
              },
            });
          }
        } catch (auditError) {
          if (logToConsole) {
            logger.debug("Failed to log permission denial:", auditError);
          }
        }
        // auditLogService.logPermissionDenial({
        //   resource: detail.url,
        //   action: detail.method,
        //   timestamp: new Date().toISOString(),
        //   userId: getCurrentUserId(),
        // });
      } catch (error) {
        if (logToConsole) {
          logger.debug(
            "[Permission Handler] Error processing permission event:",
            error,
          );
        }
      }
    },
    [enabled, isAuthenticated, toastOptions, onPermissionDenied, logToConsole],
  );

  useEffect(() => {
    if (!enabled) return;

    window.addEventListener(
      "app:permission-required",
      handlePermissionError as EventListener,
    );

    return () => {
      window.removeEventListener(
        "app:permission-required",
        handlePermissionError as EventListener,
      );
    };
  }, [enabled, handlePermissionError]);

  useEffect(() => {
    if (!enabled) return;

    const handleSuppressedFeatureDenial = async (event: Event) => {
      try {
        const customEvent = event as CustomEvent<Record<string, unknown>>;
        await trackSuppressedFeatureDenial(customEvent.detail || {});
      } catch (error) {
        if (logToConsole) {
          logger.debug(
            "[Permission Handler] Failed to export suppressed feature denial telemetry:",
            error,
          );
        }
      }
    };

    window.addEventListener(
      "app:feature-denial-audit-suppressed",
      handleSuppressedFeatureDenial as EventListener,
    );

    return () => {
      window.removeEventListener(
        "app:feature-denial-audit-suppressed",
        handleSuppressedFeatureDenial as EventListener,
      );
    };
  }, [enabled, logToConsole]);
}

/**
 * Clear recent error tracking (useful for testing)
 */
export function clearPermissionErrorCache() {
  recentErrors.clear();
}
